import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleCheckBig,
  MoreHorizontal,
  TriangleAlert,
  X,
} from 'lucide-react';
import ActivityTimeline from './ActivityTimeline';
import CommentThread from './CommentThread';
import ControlLink from './ControlLink';
import { DepartmentBadge, StatePill } from './ProgressParts';
import RichTextField from './RichTextField';
import TitleAutosave from './TitleAutosave';
import { BlockedBadge, MemberAvatar } from './WorkCardMeta';
import { useMembers } from '../wiki/use-member-names';
import { LoadingRows, WorkPageError } from './page-states';
import { useOverlays } from './overlay-context';
import { useProjectDecorations } from './use-project-decorations';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ApiError, apiFetch, v1Path } from '../../lib/api';
import { isActivityDto, type ActivityDto } from '../../lib/activity-timeline';
import { uploadPendingAttachment } from '../../lib/attachments';
import { isCommentDto, type CommentDto } from '../../lib/comments';
import { formatDateOnly } from '../../lib/dates';
import {
  archiveInitiative,
  deleteInitiative,
  deleteInitiativeMessage,
  INITIATIVES_PATH,
  isRequirementKey,
  linkedTaskCount,
  unarchiveInitiative,
} from '../../lib/initiatives';
import { useSession } from '../session-context';
import { isStepDone, launchCountdown, launchSteps } from '../../lib/launch';
import { loadStatesById } from '../../lib/initiative-streams';
import { progressPercent } from '../../lib/progress';
import LaunchPlan from './LaunchPlan';
import { OwnerPicker } from './InitiativeOwners';
import { isRichTextEmpty } from '../../lib/rich-text';
import { cn } from '../../lib/utils';
import {
  memberDisplayName,
  parseItems,
  type ListResponse,
  type MemberDto,
  type StateDto,
  type StreamRollup,
  type WorkItemFull,
  type WorkItemListDecorations,
  type WorkItemSlim,
} from '../../lib/work-types';

type LinkedRow = WorkItemSlim & WorkItemListDecorations;

const PAGE_LIMIT = 100;

/** Open work first, then done, then cancelled. */
function requirementRank(state: StateDto | undefined): number {
  return state?.group === 'completed' ? 1 : state?.group === 'cancelled' ? 2 : 0;
}

/** The tasks linked to an initiative, across every stream. Subtasks stay on their tasks. */
function useInitiativeWork(initiativeId: string | undefined) {
  const [tasks, setTasks] = useState<LinkedRow[]>([]);
  const [statesById, setStatesById] = useState<Map<string, StateDto>>(new Map());
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!initiativeId) return;
    try {
      const items: LinkedRow[] = [];
      for (let offset = 0; ; offset += PAGE_LIMIT) {
        const page = await apiFetch<ListResponse<LinkedRow>>(
          `${v1Path('/work-items')}?initiative_ids=${initiativeId}&show_sub_items=false&limit=${PAGE_LIMIT}&offset=${offset}`,
        );
        items.push(...page.items);
        if (page.items.length < PAGE_LIMIT) break;
      }
      setStatesById(await loadStatesById(Array.from(new Set(items.map((row) => row.project_id)))));
      setTasks(items.filter((row) => row.parent_id === null));
      setFailed(false);
    } catch {
      setTasks([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  useEffect(() => {
    setLoading(true);
    void load();
    const onChanged = () => void load();
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  return { tasks, statesById, loading, failed };
}

function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('rounded-xl border bg-card p-4 text-card-foreground', className)}>{children}</section>;
}

function Bar({ percent, className }: { percent: number; className?: string }) {
  return (
    <div
      className={cn('h-2 overflow-hidden rounded-full bg-muted', className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <div
        className={cn('h-full rounded-full', percent === 100 ? 'bg-emerald-500' : 'bg-primary')}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function LaunchSummary({
  rollup,
  dueDate,
  countdown,
  overdue,
  readOnly,
  onDueDateChange,
}: {
  rollup: StreamRollup;
  dueDate: string | null;
  countdown: string;
  overdue: boolean;
  readOnly: boolean;
  onDueDateChange: (value: string | null) => void;
}) {
  const dateInput = useRef<HTMLInputElement>(null);
  const openPicker = () => {
    const el = dateInput.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  };
  const percent = progressPercent(rollup);
  const remaining = Math.max(rollup.total - rollup.done, 0);
  const status =
    rollup.total === 0
      ? { label: 'No requirements yet', className: 'bg-muted text-muted-foreground', Icon: Circle }
      : remaining === 0
        ? {
            label: 'Ready to launch',
            className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100',
            Icon: CircleCheckBig,
          }
        : {
            label: `${remaining} ${remaining === 1 ? 'requirement' : 'requirements'} to go`,
            className: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
            Icon: TriangleAlert,
          };
  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_15rem]">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Overall progress</h2>
          <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium', status.className)}>
            <status.Icon className="h-3.5 w-3.5" aria-hidden />
            {status.label}
          </span>
        </div>
        <div className="mt-4 flex items-center gap-4">
          <Bar percent={percent} className="h-2.5 flex-1" />
          <span className="text-2xl font-semibold tabular-nums">{percent}%</span>
        </div>
      </div>
      <div className="relative md:border-l md:pl-5">
        <h2 className="text-sm font-semibold">Target launch</h2>
        <button
          type="button"
          disabled={readOnly}
          onClick={openPicker}
          aria-label={`Target launch: ${dueDate ? formatDateOnly(dueDate) ?? dueDate : 'Not set'}`}
          className={cn(
            'mt-3 flex items-center gap-2 rounded-md text-lg font-semibold',
            !readOnly && 'hover:text-primary',
            !dueDate && 'text-muted-foreground',
          )}
        >
          <CalendarDays className="h-5 w-5 text-muted-foreground" aria-hidden />
          {dueDate ? formatDateOnly(dueDate) ?? dueDate : 'Not set'}
        </button>
        <p className={cn('mt-1 text-xs text-muted-foreground', overdue && 'text-red-600')}>
          {dueDate || readOnly ? countdown : 'Click to set a launch date'}
        </p>
        <input
          ref={dateInput}
          type="date"
          tabIndex={-1}
          aria-hidden
          value={dueDate ?? ''}
          disabled={readOnly}
          onChange={(event) => onDueDateChange(event.target.value || null)}
          className="pointer-events-none absolute bottom-0 left-5 h-0 w-0 opacity-0"
        />
      </div>
    </div>
  );
}

function RequirementRow({
  row,
  state,
  members,
}: {
  row: LinkedRow;
  state: StateDto | undefined;
  members: Map<string, MemberDto>;
}) {
  const assignees = row.assignee_ids.map((id) => members.get(id)).filter((member): member is MemberDto => Boolean(member));
  const done = state?.group === 'completed';
  const cancelled = state?.group === 'cancelled';
  return (
    <li>
      <ControlLink
        workItemId={row.id}
        workItemKey={row.key}
        projectId={row.project_id}
        className="flex items-center gap-3 rounded-md px-2 py-2 no-underline hover:bg-muted/50 hover:no-underline"
      >
        {done ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Complete" />
        ) : (
          <Circle className="h-4 w-4 shrink-0 text-muted-foreground/60" aria-label="Not complete" />
        )}
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{row.key}</span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-sm text-foreground',
            cancelled && 'line-through text-muted-foreground',
          )}
        >
          {row.title}
        </span>
        <BlockedBadge reason={row.blocked_reason} className="hidden max-w-[14rem] shrink md:inline-flex" />
        <span className="hidden w-44 shrink-0 items-center gap-1.5 sm:flex">
          {assignees.length === 0 ? (
            <span className="text-xs text-muted-foreground">Unassigned</span>
          ) : (
            <>
              <span className="flex -space-x-1.5">
                {assignees.slice(0, 3).map((member) => (
                  <MemberAvatar key={member.user_id} member={member} className="ring-2 ring-card" />
                ))}
              </span>
              <span className="truncate text-xs text-foreground">
                {assignees.map((member) => memberDisplayName(member)).join(', ')}
              </span>
            </>
          )}
        </span>
        {state ? <StatePill name={state.name} group={state.group} className="shrink-0" /> : null}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </ControlLink>
    </li>
  );
}

function Departments({
  rollup,
  tasks,
  statesById,
  loading,
  failed,
}: {
  rollup: StreamRollup;
  tasks: LinkedRow[];
  statesById: Map<string, StateDto>;
  loading: boolean;
  failed: boolean;
}) {
  const members = useMembers();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (projectId: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  const tasksByProject = useMemo(() => {
    const map = new Map<string, LinkedRow[]>();
    for (const row of tasks) {
      const list = map.get(row.project_id) ?? [];
      list.push(row);
      map.set(row.project_id, list);
    }
    map.forEach((list) =>
      list.sort((left, right) => requirementRank(statesById.get(left.state_id)) - requirementRank(statesById.get(right.state_id))),
    );
    return map;
  }, [statesById, tasks]);
  const hiddenElsewhere = failed ? 0 : Math.max(rollup.total + rollup.cancelled - tasks.length, 0);

  return (
    <div>
      {loading ? <LoadingRows /> : null}
      {!loading && rollup.departments.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Nothing is linked yet. Open a task in any stream and set its Initiative to this one.
        </p>
      ) : null}
      {!loading ? (
        <ul className="divide-y overflow-hidden rounded-lg border">
          {rollup.departments.map((department) => {
            const percent = progressPercent(department);
            const linked = tasksByProject.get(department.project_id) ?? [];
            const rows = linked.filter((row) => statesById.get(row.state_id)?.group !== 'cancelled');
            const expanded = open.has(department.project_id);
            const people = Array.from(new Set(rows.flatMap((row) => row.assignee_ids)))
              .map((id) => members.get(id))
              .filter((member): member is MemberDto => Boolean(member));
            return (
              <li key={department.project_id}>
                <button
                  type="button"
                  onClick={() => toggle(department.project_id)}
                  aria-expanded={expanded}
                  className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40"
                >
                  <ChevronRight
                    className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')}
                    aria-hidden
                  />
                  <DepartmentBadge projectId={department.project_id} name={department.project_name} size="md" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{department.project_name}</span>
                  {people.length > 0 ? (
                    <span className="hidden -space-x-1.5 md:flex" aria-label={`${people.length} working on this`}>
                      {people.slice(0, 4).map((member) => (
                        <MemberAvatar key={member.user_id} member={member} className="ring-2 ring-card" />
                      ))}
                    </span>
                  ) : null}
                  <Bar percent={percent} className="hidden w-40 sm:block" />
                  <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
                    {department.done} / {department.total}
                  </span>
                  <span className="w-10 text-right text-xs font-medium tabular-nums">{percent}%</span>
                </button>
                {expanded ? (
                  <div className="border-t bg-muted/20 px-3 py-2">
                    {rows.length > 0 ? (
                      <ul>
                        {rows.map((row) => (
                          <RequirementRow key={row.id} row={row} state={statesById.get(row.state_id)} members={members} />
                        ))}
                      </ul>
                    ) : (
                      <p className="px-2 py-2 text-xs text-muted-foreground">
                        {failed
                          ? 'Could not load these tasks. Refresh to try again.'
                          : linked.length > 0
                            ? 'Everything linked from this stream was cancelled.'
                            : 'You are not a member of this stream, so its tasks are hidden.'}
                      </p>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {!loading && hiddenElsewhere > 0 ? (
        <p className="mt-3 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          {hiddenElsewhere} more {hiddenElsewhere === 1 ? 'requirement is' : 'requirements are'} in streams you are not a
          member of.
        </p>
      ) : null}
    </div>
  );
}

function Discussion({
  projectId,
  item,
  members,
}: {
  projectId: string;
  item: WorkItemFull;
  members: MemberDto[];
}) {
  const { toast } = useOverlays();
  const { labels, subtypes } = useProjectDecorations(projectId);
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [activity, setActivity] = useState<ActivityDto[]>([]);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const readOnly = Boolean(item.archived_at);
  const mentionSuggestions = useMemo(
    () => members.map((member) => ({ id: member.user_id, label: memberDisplayName(member) })),
    [members],
  );
  const uploadImage = useCallback((file: File) => uploadPendingAttachment(file).then((row) => row.download_url), []);

  const load = useCallback(async () => {
    const [commentPage, activityPage] = await Promise.all([
      apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${item.id}/comments`)),
      apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${item.id}/activity`)),
    ]);
    setComments(parseItems(commentPage, isCommentDto, 'comments'));
    setActivity(parseItems(activityPage, isActivityDto, 'activity'));
  }, [projectId, item.id]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load, item.version]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (isRichTextEmpty(body) || readOnly) return;
    setSubmitting(true);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${item.id}/comments`), {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setBody('');
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="mb-3 text-sm font-semibold">Comments</h2>
        <form onSubmit={submit} className="space-y-2">
          <RichTextField
            variant="compact"
            value={body}
            readOnly={readOnly}
            placeholder="Write a comment. Type @ to mention someone."
            mentions={mentionSuggestions}
            upload={uploadImage}
            onUploadError={toast}
            onChange={setBody}
          />
          <Button type="submit" size="sm" disabled={submitting || isRichTextEmpty(body) || readOnly}>
            Post comment
          </Button>
        </form>
        <div className="mt-4">
          <CommentThread comments={comments} members={members} mentionSuggestions={mentionSuggestions} />
        </div>
      </Card>
      <Card>
        <button
          type="button"
          aria-expanded={activityOpen}
          onClick={() => setActivityOpen((open) => !open)}
          className="flex w-full items-center gap-1.5 text-left text-sm font-semibold"
        >
          {activityOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
          Activity
          <span className="text-xs font-normal tabular-nums text-muted-foreground">{activity.length}</span>
        </button>
        {activityOpen ? (
          <div className="mt-3">
            <ActivityTimeline key={item.id} activity={activity} members={members} labels={labels} formatOptions={{ subtypes }} />
          </div>
        ) : null}
      </Card>
    </div>
  );
}

export default function InitiativeDetail({
  projectId,
  workItemId,
  error: outerError,
}: {
  /** The hidden Initiatives workspace. */
  projectId: string | undefined;
  workItemId: string | undefined;
  error?: string | null;
}) {
  const navigate = useNavigate();
  const { toast, confirm } = useOverlays();
  const { isOrgAdmin } = useSession();
  const { members } = useProjectDecorations(projectId);
  const [item, setItem] = useState<WorkItemFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const work = useInitiativeWork(item?.id);
  const today = useMemo(() => new Date(), []);

  const load = useCallback(async () => {
    if (!projectId || !workItemId) return;
    setError(null);
    try {
      setItem(await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${workItemId}`)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load initiative');
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, workItemId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    const onChanged = () => {
      if (!projectId || !workItemId) return;
      void apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${workItemId}`))
        .then(setItem)
        .catch(() => undefined);
    };
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [projectId, workItemId]);

  const patch = useCallback(
    async (fields: Record<string, unknown>) => {
      if (!item || !projectId) return undefined;
      try {
        const dto = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`), {
          method: 'PATCH',
          body: JSON.stringify({ version: item.version, ...fields }),
        });
        setItem(dto);
        window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
        return dto;
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const fresh = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`));
          setItem(fresh);
          toast('Someone else updated this initiative. Reloaded.');
          return fresh;
        }
        toast(err instanceof Error ? err.message : 'Update failed');
        throw err;
      }
    },
    [item, projectId, toast],
  );

  async function onArchive() {
    if (!item || !projectId) return;
    const ok = await confirm({
      title: 'Archive initiative',
      description: `Archive ${item.key}? It leaves the Initiatives list and becomes read-only. You can restore it from the Archived view.`,
      confirmLabel: 'Archive',
    });
    if (!ok) return;
    try {
      const dto = await archiveInitiative(projectId, item);
      setItem({ ...item, ...dto, description: item.description });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      toast(`Archived ${item.key}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Archive failed');
    }
  }

  async function onUnarchive() {
    if (!item || !projectId) return;
    try {
      const dto = await unarchiveInitiative(projectId, item);
      setItem({ ...item, ...dto, description: item.description });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      toast('Restored from archive');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Unarchive failed');
    }
  }

  async function onDelete() {
    if (!item || !projectId) return;
    const ok = await confirm({
      title: 'Delete initiative',
      description: deleteInitiativeMessage(item.key, linkedTaskCount(item.initiative_rollup)),
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteInitiative(projectId, item.id);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      toast('Deleted');
      navigate(INITIATIVES_PATH);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  if (!workItemId) return null;
  if (item && item.type !== 'initiative') {
    return <Navigate to={`/projects/${item.project_id}/work-items/${item.id}`} replace />;
  }

  const rollup: StreamRollup = item?.initiative_rollup ?? { total: 0, done: 0, started: 0, cancelled: 0, departments: [] };
  const readOnly = Boolean(item?.archived_at);
  const steps = item ? launchSteps(work.tasks, item.id) : [];
  const launched =
    steps.length > 0 &&
    steps.every((row) =>
      isRequirementKey(row.key) ? isStepDone(work.statesById.get(row.state_id)) : Boolean(row.live_at),
    );
  const countdown = launchCountdown(item?.due_date ?? null, launched, today);
  const overdue = countdown.endsWith('overdue');

  return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <Link
            to={INITIATIVES_PATH}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground no-underline hover:text-foreground hover:no-underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Initiatives
          </Link>
          {item ? (
            <div className="flex items-center gap-1">
              {isOrgAdmin ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" aria-label="More actions">
                      <MoreHorizontal className="h-4 w-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {readOnly ? (
                      <DropdownMenuItem onSelect={() => void onUnarchive()}>Unarchive</DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => void onArchive()}>Archive</DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="text-destructive" onSelect={() => void onDelete()}>
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <Button asChild variant="ghost" size="icon" aria-label="Close">
                <Link to={INITIATIVES_PATH}>
                  <X className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>
          ) : null}
        </div>

        {loading && !outerError ? <LoadingRows /> : null}
        <WorkPageError error={outerError ?? error} projectId={projectId} onJoined={() => void load()} />

        {item && projectId ? (
          <>
            <header className="space-y-3">
              <div className="flex items-center gap-2">
                <p className="font-mono text-xs text-muted-foreground">{item.key}</p>
                {readOnly ? (
                  <span className="rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">Archived</span>
                ) : null}
              </div>
              <TitleAutosave projectId={projectId} item={item} onUpdated={setItem} readOnly={readOnly} size="large" />
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-muted-foreground">Owners</span>
                <OwnerPicker
                  value={item.assignee_ids}
                  members={members}
                  disabled={readOnly}
                  onChange={(ids) => void patch({ assignee_ids: ids }).catch(() => undefined)}
                />
              </div>
            </header>

            <LaunchPlan
              summary={
                <LaunchSummary
                  rollup={rollup}
                  dueDate={item.due_date ?? null}
                  countdown={countdown}
                  overdue={overdue}
                  readOnly={readOnly}
                  onDueDateChange={(value) => void patch({ due_date: value }).catch(() => undefined)}
                />
              }
              initiative={item}
              tasks={work.tasks}
              statesById={work.statesById}
              loading={work.loading}
              readOnly={readOnly}
              streamView={
                <Departments
                  rollup={rollup}
                  tasks={work.tasks}
                  statesById={work.statesById}
                  loading={work.loading}
                  failed={work.failed}
                />
              }
            />
            <Discussion projectId={projectId} item={item} members={members} />
          </>
        ) : null}
      </div>
  );
}
